import React, { useEffect, useRef, useState } from 'react';
import { OnlyRenderOnClient } from '@gumtree/ui-library';
import Icon from '@gumtree/ui-library/src/icon/icon';
import styled from '@emotion/styled';
import { colors, fontSizes, fontWeights } from '@gumtree/ui-library/src/base/theme';
import { AccordionHeader } from '@gumtree/ui-library/src/accordion/styles';
import { useDispatch } from 'react-redux';
import { css } from '@emotion/react';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';

import {
    ClearAll,
    IconContainer,
    MenuList,
    MultiSelectAccordionContainer,
    MultiSelectAccordionHeader,
} from './multi-filters-frame.style';
import type { Filter, FiltersDictionary, FilterKey } from '../../types';
import { MenuItem } from '../multi-filters/dropdown.style';
import { setSingleFilterState, setFilterThunk } from '../../reducers/current-filter-states';
import { SrpUseDispatch } from '../../reducers';

const SelectedValue = styled.span`
    color: ${colors.bark60};
    font-size: ${fontSizes.small};
    font-weight: ${fontWeights.normal};
    display: -webkit-box;
    -webkit-line-clamp: 1; /* 显示的行数 */
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const FilterItem = styled(MenuItem)`
    color: ${colors.black};
`;

const MultiSelectAccordion = ({
    currentFilterStates,
    filtersDictionary,
    filterKey,
}: {
    currentFilterStates: Filter;
    filtersDictionary: FiltersDictionary;
    filterKey: FilterKey;
}) => {
    const filter = filtersDictionary[filterKey];
    const currentValue = (currentFilterStates[filterKey] as (string | number)[]) || [];
    const currentValueStore = useRef(currentValue);
    const selectedTitle = filter.label;

    const [open, setOpen] = useState<boolean>(false);
    const dispatch = useDispatch() as SrpUseDispatch;
    const close = () => {
        setOpen(false);
        if (currentValueStore.current.join(',') !== currentValue.join(',')) {
            dispatch(setFilterThunk({ key: filterKey, value: currentValue }));
        }
    };
    const handleSelect = (filter) => {
        const value = [...currentValue];
        const index = value.indexOf(filter.value);
        if (index !== -1) {
            value.splice(index, 1);
        } else {
            value.push(filter.value);
        }
        dispatch(setSingleFilterState({ key: filterKey, value }));
    };
    const isSelected = (value) => currentValue.includes(value);
    const clear = () => {
        if (!currentValue.length) return;
        dispatch(setSingleFilterState({ key: filterKey, value: [] }));
    };

    const showFilter = () => {
        setOpen(true);
        trackGA4Event<GA4.SearchFilterEvent>({
            event: 'expand_search_filter',
            filterName: filterKey,
        });
    };

    useEffect(() => {
        trackGA4Event<GA4.SearchFilterEvent>({
            event: 'impress_search_filter',
            filterName: filterKey,
        });
    }, []);
    const selectedLabel = currentValue
        .map((value) => filter?.values.find((item) => item.value === value)?.label || '')
        .filter(Boolean);

    if (!filter.values || filter.values.length === 0) return null;
    return (
        <React.Fragment key={filterKey}>
            <AccordionHeader
                css={css`
                    border-bottom: 1px solid rgb(216, 214, 217);
                `}
                onClick={showFilter}
            >
                <div>
                    <div>{selectedTitle}</div>
                    <SelectedValue>{selectedLabel.join(', ')}</SelectedValue>
                </div>
                <Icon type="chevron-r" />
            </AccordionHeader>
            <OnlyRenderOnClient>
                <MultiSelectAccordionContainer isOpen={open}>
                    <MultiSelectAccordionHeader>
                        <IconContainer onClick={close}>
                            <Icon type="arrow-left" />
                        </IconContainer>
                        <div>{selectedTitle}</div>
                        <ClearAll onClick={clear}>Clear All</ClearAll>
                    </MultiSelectAccordionHeader>
                    <MenuList>
                        {filter.values.map((filter) =>
                            filter.value ? (
                                <FilterItem
                                    onClick={() => handleSelect(filter)}
                                    key={filter.value}
                                    className={isSelected(filter.value) ? 'active' : ''}
                                >
                                    <span>{filter.label}</span>
                                    <div className="check-box">
                                        <Icon type="check" color="white" />
                                    </div>
                                </FilterItem>
                            ) : null
                        )}
                    </MenuList>
                </MultiSelectAccordionContainer>
            </OnlyRenderOnClient>
        </React.Fragment>
    );
};

export default MultiSelectAccordion;
